import {
  FC,
  Suspense,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSnapshot } from 'valtio'
import styled from 'styled-components'
import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  Ads,
  Sports,
  ChannelContent,
  ChannelDetailed,
  Placement,
  UserRoles,
  useGetAdvertisementLazyQuery,
  useGetPrivateChannelDataLazyQuery,
  useGetSportsLazyQuery,
  usePrivateChannelContentLazyQuery,
  useUnfollowChannelMutation
} from '@app/graphql'
import {
  Feed,
  FeedPinnedContent,
  NavigationMenu,
  Screen,
  ScreenHeaderWithInbox,
} from '@app/components'
import {
  PrivateChannelState,
  PublicChannelState,
  CurrentUserState,
  showSignupPromptModal,
  CurrentCountryState,
  guestMode,
} from '@app/storage'
import { Confirmation, Icon, IconColor, IconTypes, Loader, Modal, Popup } from '@app/ui'
import { ROUTES, SEARCH_PARAMS, INSTALL_PROMPT_STORAGE_KEY } from '@app/constants'
import PrivateChannelList from './PrivateChannelList'
import { ButtonType, ModalType } from '@app/types'
import { ChannelListModalState, closeChannelListModal } from '@app/storage/channelListModal'
import { getExcludedAdIds } from '@app/helpers/adFrequency'
import { TipsterOnboarding } from '@app/components/Onboarding/TipsterOnboarding'
import { useInstallPrompt } from '@app/hooks/useInstallPrompt'

interface PrivateChannelProps {}

const PrivateChannel: FC<PrivateChannelProps> = () => {
  const limit = 10
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    id,
    pinnedContent,
    channelData,
    offset,
    channelsList,
    hasMoreData,
    adsData,
    sports,
    prevOffset,
  } = useSnapshot(PrivateChannelState)

  const {
    countryId,
    userType,
    followedChannels,
    ownedChannels,
    currentChannelId
  } = useSnapshot(CurrentUserState)
  const { id: currentCountryId }= useSnapshot(CurrentCountryState)
  const newChannelId = localStorage.getItem('newChannelId')
  const [ searchParams, setSearchParams ] = useSearchParams(
    newChannelId ? { [SEARCH_PARAMS.channelId]: newChannelId }: {}
  )
  const screenUpRef = useRef(null)
  // USE_STATE
  const { opened: channelListState } = useSnapshot(ChannelListModalState)
  const [ showConfirmPopUp, setShowConfirmPopUp ] = useState(false)
  const [ channelToDelete, setChannelToDelete ] = useState<ChannelDetailed>(null)
  const [ dataLoading, setDataLoading ] = useState(false)
  const [ initialLoading, setInitialLoading ] = useState(false)
  const [ loadError, setLoadError ] = useState(false)
  const [ newFollowingPopup, setNewFollowingPopup ] = useState(Boolean(newChannelId))
  // QUERIES
  const [ getPrivateChannelContent ] = usePrivateChannelContentLazyQuery({ fetchPolicy: 'no-cache' })
  const [ getPrivateChannelData ] = useGetPrivateChannelDataLazyQuery({ fetchPolicy: 'no-cache' })
  const [ getSports ] = useGetSportsLazyQuery()
  const [ getAds ] = useGetAdvertisementLazyQuery({ fetchPolicy: 'network-only' })
  const [ unfollowChannel ] = useUnfollowChannelMutation()

  const [ openTipsCreation, setOpenTipsCreation ] = useState(false)
  const [ openPostCreation, setOpenPostCreation ] = useState(false)
  const [ openPollCreation, setOpenPollCreation ] = useState(false)

  useEffect(() => {
    getInitialData(currentChannelId)
  }, [ currentChannelId ])
  const allUserChannels = useMemo(() => {
    return Object.values(
      [ ...ownedChannels, ...followedChannels ].reduce((channels, channel) => {
        channels[channel.id] = channel
        return channels
      }, {})
    )
  }, [ ownedChannels, followedChannels ])

  useEffect(() => {
    if(localStorage.getItem('newChannelId')) {
      setNewFollowingPopup(true)
    }
  }, [ newChannelId ])


  useLayoutEffect(() => {
    if (screenUpRef?.current) {
      screenUpRef.current.scrollIntoView({ block: 'start' })
    }
  }, [ screenUpRef?.current, currentChannelId ])

  useEffect(() => {
    if (PublicChannelState.isPublic) PublicChannelState.isPublic = false

    const channels = Object.values(
      [ ...ownedChannels, ...followedChannels ].reduce((channels, channel) => {
        channels[channel.id] = channel
        return channels
      }, {})
    )

    if (channels.length > 0 && !id) {
      setChannelData()
    }
  }, [ ownedChannels.length, followedChannels.length, id ])

  useEffect(() => {
    if (currentChannelId)
      setSearchParams({ [SEARCH_PARAMS.channelId]: currentChannelId })
  }, [ currentChannelId ])

  const onCloseChannelSearchModal = () => {
    closeChannelListModal()
  }

  const confirmUnFollowChannel = () => {
    setShowConfirmPopUp(false)
    unfollowChannel({ variables: { channelId: channelToDelete.id } })
      .catch(e => console.error(e))
      .finally(() => {
        setShowConfirmPopUp(false)
        setChannelToDelete(null)
        window.location.reload()
      })
  }

  const setChannelData = async () => {
    const channelIdFromParams = searchParams.get(SEARCH_PARAMS.channelId)

    const getChannel = (list: ChannelDetailed[]) => {
      let channel: ChannelDetailed = list[0]
      if(currentChannelId) {
        channel = list.find((ch) => ch.id === currentChannelId)
      }
      if(channelIdFromParams) {
        channel = list.find((ch) => ch.id === channelIdFromParams)
      }
      return channel
    }
    try {
      setInitialLoading(true)
      const channels = allUserChannels

      if (channels?.length) {
        const userChannels = channels as ChannelDetailed[]
        PrivateChannelState.channelsList = userChannels
        const channel = getChannel(userChannels)
        CurrentUserState.ownedChannels = userChannels
        await saveChannelData(channel)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setInitialLoading(false)
    }
  }

  const getInitialData = async (channelId: string) => {
    setDataLoading(true)

    try {
      const [ sportsPromise, channelPromise, adsPromise ] = [
        getSports(),
        getPrivateChannelData({
          variables: { channelId, limit },
        }),
        getAds({
          variables: {
            channelId,
            placement: Placement.Channel,
            filter: {
              isActive: true,
              channelIds: [ channelId ],
              countryIds: [ currentCountryId || countryId ],
            },
          },
        }),
      ]

      const [ sportsRes, channelRes, adsRes ] = await Promise.allSettled([
        sportsPromise,
        channelPromise,
        adsPromise,
      ])

      const sports =
        sportsRes.status === 'fulfilled' ? sportsRes.value?.data?.sports ?? [] : []

      const privateChannelContent =
        channelRes.status === 'fulfilled'
          ? channelRes.value?.data?.privateChannelContent ?? {}
          : {}

      const channelDetailed =
        channelRes.status === 'fulfilled'
          ? channelRes.value?.data?.channelDetailed ?? {}
          : {}

      const adsByPlacement =
        adsRes.status === 'fulfilled'
          ? adsRes.value?.data?.adsByPlacement ?? []
          : []

      const feed = privateChannelContent?.feed ?? []
      const nextCursor = privateChannelContent?.nextCursor ?? null
      const prevCursor = privateChannelContent?.previousCursor ?? null

      Object.assign(PrivateChannelState, {
        channelData: feed as ChannelContent[],
        offset: nextCursor,
        hasMoreData: Boolean(nextCursor),
        sports,
        pinnedContent: channelDetailed?.pinnedContent ?? null,
        adsData: adsByPlacement,
        prevOffset: prevCursor,
        id: channelId,
      })
    } catch (error) {
      console.error('Failed to fetch initial channel data:', error)
    } finally {
      setDataLoading(false)
    }
  }

  const saveChannelData = async (channel: ChannelDetailed) => {
    if (!channel) return
    PrivateChannelState.id = channel.id
    PrivateChannelState.logo = channel.logo
    PrivateChannelState.title = channel.channelName
    PrivateChannelState.pinnedContent = channel.pinnedContent

    await getInitialData(channel.id)
    localStorage.setItem('currentChannelId', channel.id)
  }

  const getNextData = async () => {
    try {
      if(!offset) return
      PrivateChannelState.prevOffset = prevOffset

      setDataLoading(true)
      const response = await getPrivateChannelContent({
        variables: {
          channelId: currentChannelId,
          limit,
          cursor: { id: offset?.id, createdAt: offset?.createdAt },
        },
      })
      const { privateChannelContent } = response.data
      const { feed, nextCursor, previousCursor } = privateChannelContent ?? {}
      if (response.error) {
        setLoadError(true)
      } else if (!feed?.length) {
        PrivateChannelState.hasMoreData = false
      } else {
        PrivateChannelState.offset = nextCursor
        PrivateChannelState.prevOffset = previousCursor
        PrivateChannelState.channelData = [
          ...channelData,
          ...feed,
        ] as ChannelContent[]
        PrivateChannelState.hasMoreData = !!nextCursor

        const currentAdsData = PrivateChannelState.adsData
        const adConfigs = currentAdsData.filter((a) => !!a.id).map((a) => ({
          id: a.id,
          frequencyCapImpressions: a.frequencyCapImpressions ?? null,
          frequencyCapWindow: a.frequencyCapWindow ?? null,
          rotationWeight: a.rotationWeight ?? 5,
        }))
        const cappedIds = getExcludedAdIds(adConfigs)
        const alreadyFetchedIds = currentAdsData.map((a) => a.id)
        const excludeIds = Array.from(new Set([ ...cappedIds, ...alreadyFetchedIds ]))
        const adsResult = await getAds({
          variables: {
            channelId: currentChannelId,
            placement: Placement.Channel,
            excludeIds,
            filter: {
              isActive: true,
              channelIds: [ currentChannelId ],
              countryIds: [ currentCountryId || countryId ],
            },
          },
        })
        const freshAds = (adsResult.data?.adsByPlacement ?? []).filter(
          (a) => !currentAdsData.some((existing) => existing.id === a.id)
        )
        if (freshAds.length) {
          PrivateChannelState.adsData = [ ...currentAdsData, ...freshAds ]
        }
      }
    } catch (e) {
      setLoadError(true)
      console.error(e)
    } finally {
      setDataLoading(false)
    }
  }

  const onPinnedContentClick = () => {
    navigate(
      `${ROUTES.content.short}/${pinnedContent.id}?type=${pinnedContent.contentType}`
    )
  }

  const loading = initialLoading || dataLoading

  const propmtSignupModal = showSignupPromptModal({
    message: t('incognito.modal.loginDefaultMessage'),
    title: t('incognito.modal.loginTitle'),
  })

  const isNotTipster = userType !== UserRoles.Tipster
  useInstallPrompt(INSTALL_PROMPT_STORAGE_KEY)

  return (
    <Screen
      title={t('common.feed')}
      withNavigation
      noPadding
      noFixedHeight
    >
      <div ref={screenUpRef} />
      <TipsterOnboarding onClose={() => null} />
      <ScreenHeaderWithInbox
        showBackButton
      />
      {loading && <Loader />}
      <Suspense fallback={<Loader />}>
        <Container pinnedContent={!!pinnedContent}>
          {pinnedContent && (
            <FeedPinnedContent
              pinnedContent={pinnedContent as any}
              onContentClick={onPinnedContentClick}
              className={'sticky'}
              isNotTipster={isNotTipster}/>
          )}
          <Feed
            channelData={channelData as ChannelContent[]}
            onUpdate={guestMode() ? propmtSignupModal : () => getInitialData(currentChannelId)}
            loading={loading}
            loadError={loadError}
            hasMore={hasMoreData}
            loadContent={getNextData}
            adsData={adsData as Partial<Ads>[]}
            pinContentAccess={[ UserRoles.Admin, UserRoles.Tipster ]}
            createContentAccess={[ UserRoles.Admin, UserRoles.Tipster ]}
            sportData={sports as Sports[]}
            setOpenTipsCreation={setOpenTipsCreation}
            openPostCreation={openPostCreation}
            setOpenPostCreation={setOpenPostCreation}
            openPollCreation={openPollCreation}
            setOpenPollCreation={setOpenPollCreation}
            openTipsCreation={openTipsCreation}
          />
        </Container>
      </Suspense>
      {/* MODALS */}

      {channelListState &&
        <StyledChannelListScreen type={ModalType.headerSearch} onClose={() => null} open={channelListState}>
          <PrivateChannelList
            onclose={onCloseChannelSearchModal}
            hasChannel ={channelsList.length}
            showUserDataInInboxHeader={false}
          />
          <NavigationMenu/>

        </StyledChannelListScreen>
      }

      {
        newFollowingPopup && !loading ? (
          <Popup
            open={(() => {
              PrivateChannelState.newChannelId = null
              return newFollowingPopup
            })()}
            closeButtonText={t('common.ok').toUpperCase()}
            headerText={t('validation.followingChannel').toUpperCase()}
            onClose={() => {
              setNewFollowingPopup(false)
              localStorage.removeItem('newChannelId')
            }}
          >
            <span className="vb-font normal darkest">
              {t('channels.youHaveBeenAddedToThe', { channelName: channelsList.find( channel => channel.id === currentChannelId)?.channelName })}
            </span>
          </Popup>
        ): null
      }
      {
        <Confirmation
          open={showConfirmPopUp}
          onClose={() =>{ setShowConfirmPopUp(false)}}
          onConfirm={()=> confirmUnFollowChannel()}
          confirmButtonType={ButtonType.warning}
          headerText={t('common.attention').toUpperCase()}
          icon={<Icon type={IconTypes.alertTriangle} color={IconColor.warning} />}
        >
          {t('notification.unfollow.channel', { channelName: channelToDelete?.channelName })}
        </Confirmation>
      }
    </Screen>
  )
}

const StyledChannelListScreen = styled(Modal)`
  background-color: white;
`

const Container = styled.div<{ pinnedContent: boolean }>`
  margin-top: ${(props) => (props.pinnedContent ? '120px' : '100px')};
  min-height: calc(var(--app-height) - 176px);
  margin-bottom: 12px;
`
export default PrivateChannel
